import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { MembershipStatusEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import { Button, Dialog, Icon, cn, sonner } from '@sr/ui-library';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { membershipsQueryKeys } from '../api/memberships/membershipApi';
import { useUpdateWorkspaceDetails } from '../api/workspaces/workspaceApi';
import { MEMBERSHIP_AGREEMENT_URL } from '../constant/Common';
import { userService } from '../reducers/permissions/userSlice';
import { useTypedSelector } from '../stateStore';
import { isAllowedToApplyMembership } from '../utils/helpers/common';
import { RequestMembershipModal } from './RequestMembershipModal';

const { toast } = sonner;

enum MembershipActionsEnum {
  SubmitReview = 'submitReview',
}

type MembershipActionsMenuProps = {
  className?: string;
  handleActionComplete?: () => void;
  workspace?: Workspace;
};

export const MembershipActionsMenu = ({
  className,
  handleActionComplete,
  workspace,
}: MembershipActionsMenuProps) => {
  const [activeModal, setActiveModal] = useState<MembershipActionsEnum | null>(
    null,
  );
  const [hasAppliedForMembership, setHasAppliedForMembership] = useState(false);

  const queryClient = useQueryClient();
  const currentUser = useTypedSelector(userService.selectors.userMe);
  const updateWorkspaceDetailsMutation = useUpdateWorkspaceDetails();
  const isSubmitting = updateWorkspaceDetailsMutation.isPending;

  const membershipStatus = workspace?.membershipStatus;

  const showApplyMembership =
    !hasAppliedForMembership &&
    isAllowedToApplyMembership(workspace, currentUser);
  const showSubmitReview =
    membershipStatus === MembershipStatusEnum.AwaitingUpdate;

  const handleMembershipSuccess = () => {
    setHasAppliedForMembership(true);
    if (handleActionComplete) handleActionComplete();
  };

  const handleSubmitReview = async () => {
    if (!workspace?._id) return;

    try {
      await updateWorkspaceDetailsMutation.mutateAsync({
        id: workspace._id,
        updateWorkspaceDetailsDto: {
          membershipStatus: MembershipStatusEnum.InReview,
        },
      });

      // Invalidate the specific membership query to ensure it's refetched
      if (workspace.membershipId) {
        queryClient.invalidateQueries({
          queryKey: membershipsQueryKeys.single(workspace.membershipId),
        });
      }

      setActiveModal(null);
      if (handleActionComplete) handleActionComplete();
      toast.success('Membership submitted for review successfully.');
    } catch (error) {
      toast.error('Failed to submit membership for review. Please try again.');
    }
  };

  return (
    <div className={cn('flex gap-2', className)}>
      {showSubmitReview && (
        <Button
          disabled={isSubmitting}
          size="sm"
          onClick={() => setActiveModal(MembershipActionsEnum.SubmitReview)}
        >
          Submit Update
        </Button>
      )}

      {showApplyMembership && (
        <RequestMembershipModal
          workspace={workspace}
          onSuccess={handleMembershipSuccess}
        />
      )}

      <Button asChild size="sm" variant="outline">
        <Link
          rel="noopener noreferrer"
          target="_blank"
          to={MEMBERSHIP_AGREEMENT_URL}
        >
          View Agreement
          <Icon icon={ArrowSquareOutIcon} />
        </Link>
      </Button>

      {/* Submit for Review Modal */}
      {showSubmitReview && (
        <Dialog
          heading="Submit Update"
          actionButtonProps={{
            children: 'Submit Update',
            disabled: isSubmitting,
            loading: isSubmitting,
            onClick: handleSubmitReview,
          }}
          cancelButtonProps={{
            children: 'Cancel',
            disabled: isSubmitting,
            onClick: () => setActiveModal(null),
          }}
          description="If you've updated your workspace details and corrected all requested changes. Please submit the membership for review."
          open={activeModal === MembershipActionsEnum.SubmitReview}
          onOpenChange={(open) => {
            if (!isSubmitting) {
              setActiveModal(open ? MembershipActionsEnum.SubmitReview : null);
            }
          }}
        />
      )}
    </div>
  );
};
